import { Coordinate } from 'ol/coordinate';
import { fromLonLat } from 'ol/proj';
import { getDistance as gcDistance } from 'ol/sphere';
const _PROJECTION = (<any>window)["appenv"].projection;

export class CoordinatesUtils {
    static decimalToDegreesMinutesAndSeconds(coordinate: Coordinate, roundSeconds: boolean = false) {
        const precision = roundSeconds ? 0 : 3;

        coordinate = this.normalizeCoordinate(coordinate);
        const lonArray = this.getDegreesMinutesSeconds(coordinate[0], precision);
        const latArray = this.getDegreesMinutesSeconds(coordinate[1], precision);

        let lonFormattedArray = [
            lonArray[0],
            lonArray[1].toString().padStart(2, '0'),
            lonArray[2].toFixed(precision).padStart(3 + precision, '0')
        ];
        let latFormattedArray = [
            latArray[0],
            latArray[1].toString().padStart(2, '0'),
            latArray[2].toFixed(precision).padStart(3 + precision, '0')
        ];

        const lon = `${lonFormattedArray[0]}\u00b0 ${lonFormattedArray[1]}\u2032 ${lonFormattedArray[2]}\u2033 ${coordinate[0] < 0 ? 'W' : 'E'}`;
        const lat = `${latFormattedArray[0]}\u00b0 ${latFormattedArray[1]}\u2032 ${latFormattedArray[2]}\u2033 ${coordinate[1] < 0 ? 'S' : 'N'}`;

        return { lon, lat };
    }

    static decimalToDegreesAndMinutes(coordinate: Coordinate) {
        const precision = 3;

        coordinate = this.normalizeCoordinate(coordinate);
        const lonArray = this.getDegreesMinutesSeconds(coordinate[0], precision);
        const latArray = this.getDegreesMinutesSeconds(coordinate[1], precision);

        let lonFormattedArray = [
            lonArray[0],
            (lonArray[1] + lonArray[2] / 60).toFixed(precision).padStart(3 + precision, '0')
        ];
        let latFormattedArray = [
            latArray[0],
            (latArray[1] + latArray[2] / 60).toFixed(precision).padStart(3 + precision, '0')
        ];

        const lon = `${lonFormattedArray[0]}\u00b0 ${lonFormattedArray[1]}\u2032 ${coordinate[0] < 0 ? 'W' : 'E'}`;
        const lat = `${latFormattedArray[0]}\u00b0 ${latFormattedArray[1]}\u2032 ${coordinate[1] < 0 ? 'S' : 'N'}`;

        return { lon, lat };
    }

    static degreesToDecimal(coordinate: string): number {
        // degrees format
        // Simple: +-DDD MM SS.SSS
        // Marine: DDD° MM′ SS.SSS″ N/S/E/W
        // GPS: DDD° MM.MMM′ N/S/E/W
        // DDD - degrees, MM - minutes, SS.SSS - seconds and decimal part of seconds

        const simpleRegex = /(-?[0-9]{1,3}) ([0-9]{1,2}) ([0-9]{1,2}(?:[.][0-9]+)?)/;
        const simpleResult = simpleRegex.exec(coordinate);
        let decimalDeg = Number.MIN_SAFE_INTEGER;

        if (simpleResult) {
            const deg = parseInt(simpleResult[1]);
            decimalDeg = Math.sign(deg)*(Math.abs(deg) + parseInt(simpleResult[2]) / 60 + parseFloat(simpleResult[3]) / 3600);
        }

        // Try GPS format
        if (decimalDeg == Number.MIN_SAFE_INTEGER) {
            const gpsRegex = /([0-9]{1,3})[°]? ([0-9]{1,2}(?:[.|,][0-9]+)?)[′]? ([nNsSwWeE])/
            const gpsResult = gpsRegex.exec(coordinate);

            if (gpsResult) {
                // replace the danish , with a .
                gpsResult[2] = gpsResult[2].replace(',', '.');

                const sign = gpsResult[3].toUpperCase() === "N" || gpsResult[3].toUpperCase() === "E" ? 1 : -1;
                decimalDeg = sign*(parseInt(gpsResult[1]) + parseFloat(gpsResult[2]) / 60);
            }
        }

        // Try nautical format
        if (decimalDeg == Number.MIN_SAFE_INTEGER) {
            const nauticalRegex = /([0-9]{1,3})° ([0-9]{1,2})′ ([0-9]{1,2}(?:[.][0-9]+)?)″ ([nNsSwWeE])/;
            const nauticalResult = nauticalRegex.exec(coordinate);

            if (nauticalResult) {
                const sign = nauticalResult[4].toUpperCase() === "N" || nauticalResult[4].toUpperCase() === "E" ? 1 : -1;
                decimalDeg = sign*(parseInt(nauticalResult[1]) + parseInt(nauticalResult[2]) / 60 + parseFloat(nauticalResult[3]) / 3600);
            }
        }

        if (decimalDeg == Number.MIN_SAFE_INTEGER) {
            throw new Error("invalid numbers");
        }

        return decimalDeg;
    }

    static normalizeCoordinate(coordinate: Coordinate): Coordinate {
        let lon = Math.abs(coordinate[0]);
        const sign = Math.sign(coordinate[0]);
        while (lon > 180) { lon = lon - 360; };
        return [sign*lon, coordinate[1]];
    }

    static getDegreesMinutesSeconds(xyValue: number, precision: number): [number, number, number] {
        let degrees = Math.trunc(Math.abs(xyValue));
        let minutes = Math.trunc(Math.abs(xyValue)*60)%60;
        let seconds = (Math.abs(xyValue)*3600)%60;

        // Correct for rounding of seconds (if necessary)
        if (Number(seconds.toFixed(precision)) >= 60) {
            seconds = 0;
            minutes++;
            if (minutes >= 60) {
                minutes = 0;
                degrees++;
            }
        }

        return [
            degrees,
            minutes,
            seconds
        ];
    }

    // Note: Assumes rhumbline
    static calcCourse(fromPt: Coordinate, toPtInput: Coordinate) {
        const toPt = Array.from(toPtInput);

        // if dLon over 180° take shorter rhumb line across the anti-meridian
        const deltaLon = toPt[0] - fromPt[0];
        if (Math.abs(deltaLon) > 180) {
            toPt[0] = toPt[0] + (deltaLon < 0 ? 360 : -360);
        }

        // Project to Mercator and calculate angle on projection
        const fromProj = fromLonLat(fromPt, _PROJECTION);
        const toProj = fromLonLat(toPt, _PROJECTION);

        let course = Math.atan2(toProj[0] - fromProj[0], toProj[1] - fromProj[1]) * 180 / Math.PI;
        if (course < 0) course += 360;
        return course;
    }

    // Returns rhumbline distance in meters
    static calcRhumblineDist(fromPt: Coordinate, toPt: Coordinate)
    {
        const R = 6371e3; // metres
        const latrad1 = fromPt[1] * Math.PI/180; // latrad, lonrad in radians
        const latrad2 = toPt[1] * Math.PI/180;
        const dlatrad = (toPt[1]-fromPt[1]) * Math.PI/180;
        let dlonrad = (toPt[0]-fromPt[0]) * Math.PI/180;

        const dprojlat = Math.log(Math.tan(Math.PI/4+latrad2/2)/Math.tan(Math.PI/4+latrad1/2));
        const q = Math.abs(dprojlat) > 10e-12 ? dlatrad/dprojlat : Math.cos(latrad1); // E-W course becomes ill-conditioned with 0/0

        // if dLon over 180° take shorter rhumb line across the anti-meridian:
        if (Math.abs(dlonrad) > Math.PI) dlonrad = dlonrad>0 ? -(2*Math.PI-dlonrad) : (2*Math.PI+dlonrad);

        return Math.sqrt(dlatrad*dlatrad + q*q*dlonrad*dlonrad) * R;
    }

    // Returns GC and RL distances in nautical miles
    static calcDistance(fromPt: Coordinate, toPt: Coordinate)
    {
        return {
            rl: this.calcRhumblineDist(fromPt, toPt) / 1852,
            gc: gcDistance(fromPt, toPt) / 1852
        };
    }

    // Returns true if the distance from pt1 to pt2 is less than or equal to maxDist meters
    static isPointsWithinDist(pt1: Coordinate, pt2: Coordinate, maxDist: number)
    {
        if (pt1[0] === pt2[0] && pt1[1] === pt2[1])
        {
            return true;
        }

        const actualDist = gcDistance(pt1, pt2);
        return actualDist <= maxDist;
    }

    // Is two coordinates equal (optionally allowing for a margin)
    static isCoordinatesEqual(c1: Coordinate, c2: Coordinate, margin?: number): boolean
    {
        if (!margin)
        {
            margin = 0;
        }

        return (Math.abs(c1[0]-c2[0]) <= margin) && (Math.abs(c1[1]-c2[1]) <= margin);
    }

    /**
     * getCardinal
     * @param degree number representing the heading / course of the vessel
     * @returns string
     */
    static getCardinalDirection(degree: number) {
        var val = Math.floor((degree / 22.5) + 0.5);
        var arr = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
        return arr[(val % 16)];
    }
}
